/**
 * SubmitForReviewPage — Phase 8 (applicant-web slice).
 *
 * Confidence pass.
 *
 * The signatures section asked applicants to pre-attest "I have signed
 * in pen" BEFORE the case team has even printed the packet. That's
 * incoherent under the locked product model: the case team prints,
 * ships to the applicant, and the applicant signs the printed pages
 * and forwards to USCIS.
 *
 * This page replaces the editable signature fields with a confident
 * "Submit for review" surface organised as a partnership:
 *
 *   - "We'll handle from here" (3 steps) + 72-hour SLA chip
 *   - "When the packet arrives" (3 steps the applicant takes)
 *   - Optional soft warning if other sections still have required gaps
 *     — friendly, not blocking. The case team can work cases with gaps.
 *   - Two confirmation checkboxes with custom check graphics (truthful
 *     answers; uploaded documents). Submit button is gated on both.
 *   - Submit click invokes the supplied `onSubmit` (App.tsx wires this
 *     to the firebase-adapter's `submitApplication()` helper, which
 *     writes `caseState: 'in_review'` + `submittedAt`). Once the
 *     promise lands, the App's caseState routing swaps in
 *     `<PostSubmitView>`.
 */
import { useMemo, useState } from 'react';
import type { FormEngine } from '@inside-us-aos/form-engine';

import { reportError, reportEvent } from '../monitoring';
import styles from './SubmitForReviewPage.module.css';

export interface ISubmitForReviewPageProps {
  /** Engine — read so we can show a final required-gaps count if any */
  engine: FormEngine;
  /** Application id for the submit call. Reserved for future telemetry. */
  applicationId: string;
  /** Submit handler (writes caseState: 'in_review' + submittedAt) */
  onSubmit: () => Promise<void>;
}

interface ISubmitState {
  kind: 'idle' | 'submitting' | 'error';
  errorMessage?: string;
}

export function SubmitForReviewPage(
  props: ISubmitForReviewPageProps,
): JSX.Element {
  const { engine, onSubmit } = props;

  const [confirmedTruthful, setConfirmedTruthful] = useState(false);
  const [confirmedDocuments, setConfirmedDocuments] = useState(false);
  // UPL/filer attestation gate per ADR 0022
  // action item 3 ("Disclaimers" line). The signer-of-record
  // explicitly acknowledges that Inside US is a document-prep tool
  // and that they (not us) are the filer-of-record. Defends against
  // a later "I thought they were filing for me" claim.
  const [confirmedFiler, setConfirmedFiler] = useState(false);
  const [submitState, setSubmitState] = useState<ISubmitState>({
    kind: 'idle',
  });

  // Soft gap-count: sum required-unfilled fields across every section
  // that isn't `signatures` itself. The signatures section lives in
  // its own UX surface (this page), so its progress isn't a "gap" the
  // applicant should see flagged here.
  const gapCount = useMemo(() => {
    const progress = engine.getAllSectionProgress();
    let total = 0;
    for (const [sectionId, sp] of progress) {
      if (sectionId === 'signatures') continue;
      total += sp.missingFieldIds.length;
    }
    return total;
  }, [engine]);

  const submitDisabled =
    !confirmedTruthful ||
    !confirmedDocuments ||
    !confirmedFiler ||
    submitState.kind === 'submitting';

  const handleSubmit = async (): Promise<void> => {
    setSubmitState({ kind: 'submitting' });
    reportEvent('intake.submit_clicked', { required_gaps: gapCount });
    try {
      await onSubmit();
      setSubmitState({ kind: 'idle' });
      reportEvent('intake.submit_succeeded');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Something went wrong.';
      setSubmitState({ kind: 'error', errorMessage: message });
      // Submit failures are critical — user just clicked the final
      // button and we couldn't write to Firestore. Ship to Sentry
      // every time so we see the broken-funnel signal.
      reportError(err, {
        kind: 'intake',
        flow: 'submit_for_review',
        required_gaps: gapCount,
      });
    }
  };

  return (
    <section
      aria-label="Submit for review"
      className={styles.shell}
      data-section-id="signatures"
    >
      <div className={styles.card}>
        <header className={styles.header}>
          <p className={styles.eyebrow}>Final step</p>
          <h2 className={styles.headline}>
            You&apos;re ready to send your application
          </h2>
          <p className={styles.subhead}>
            We have everything we need to start. Here&apos;s how the next
            stretch works — your part, and ours.
          </p>
        </header>

        <div className={styles.flow}>
          <div className={styles.flow_column}>
            <p className={styles.flow_label}>We&apos;ll handle from here</p>
            <ol className={styles.steps}>
              <li>
                <span className={styles.step_num}>1</span>
                <span>Review everything you sent</span>
              </li>
              <li>
                <span className={styles.step_num}>2</span>
                <span>Assemble your USCIS filing packet</span>
              </li>
              <li>
                <span className={styles.step_num}>3</span>
                <span>Print and mail it to your address</span>
              </li>
            </ol>
            <p className={styles.sla} role="note">
              <span className={styles.sla__icon} aria-hidden="true">
                ⚡
              </span>
              <span>
                <strong>72 hours</strong> from submit to packet shipped
              </span>
            </p>
          </div>

          <div className={styles.flow_divider} aria-hidden="true" />

          <div className={styles.flow_column}>
            <p className={styles.flow_label}>When the packet arrives</p>
            <ol className={styles.steps}>
              <li>
                <span className={styles.step_num}>1</span>
                <span>
                  Sign in <strong>blue ink</strong> on each signature line
                </span>
              </li>
              <li>
                <span className={styles.step_num}>2</span>
                <span>Include the filing fees per the packet instructions</span>
              </li>
              <li>
                <span className={styles.step_num}>3</span>
                <span>Mail the complete packet to USCIS</span>
              </li>
            </ol>
          </div>
        </div>

        {gapCount > 0 ? (
          <div
            role="status"
            aria-label="Required gaps"
            className={styles.gap_warning}
            data-required-gaps={gapCount}
          >
            <span className={styles.gap_warning__icon} aria-hidden="true">
              ⚠
            </span>
            <span>
              {gapCount} required question{gapCount === 1 ? '' : 's'} still
              blank in earlier sections — that&apos;s okay, your case team
              may follow up if anything matters.
            </span>
          </div>
        ) : null}

        <div className={styles.confirmations}>
          <p className={styles.confirmations__label}>
            Three quick confirmations:
          </p>
          <label className={styles.confirmation}>
            <input
              type="checkbox"
              className={styles.confirmation__input}
              checked={confirmedTruthful}
              onChange={ev => setConfirmedTruthful(ev.target.checked)}
            />
            <span className={styles.confirmation__text}>
              I&apos;ve answered all required questions truthfully.
            </span>
          </label>
          <label className={styles.confirmation}>
            <input
              type="checkbox"
              className={styles.confirmation__input}
              checked={confirmedDocuments}
              onChange={ev => setConfirmedDocuments(ev.target.checked)}
            />
            <span className={styles.confirmation__text}>
              I&apos;ve uploaded every document I have on hand.
              <span className={styles.confirmation__helper}>
                We&apos;ll let you know if more are needed.
              </span>
            </span>
          </label>
          <label className={styles.confirmation}>
            <input
              type="checkbox"
              className={styles.confirmation__input}
              checked={confirmedFiler}
              onChange={ev => setConfirmedFiler(ev.target.checked)}
            />
            <span className={styles.confirmation__text}>
              I understand Greenbroad is a document-preparation tool —
              not a law firm — and that <strong>I am the filer</strong>{' '}
              who signs the packet under penalty of perjury and mails
              it to USCIS myself.
              <span className={styles.confirmation__helper}>
                See our Terms of Service for the full scope. If your
                case is complex (criminal, prior removal,
                misrepresentation) talk to an attorney first.
              </span>
            </span>
          </label>
        </div>

        {submitState.kind === 'error' ? (
          <div role="alert" className={styles.error}>
            <span>
              Couldn&apos;t send: {submitState.errorMessage ?? 'Unknown error'}
            </span>
            <button
              type="button"
              className={styles.error__retry}
              onClick={() => {
                void handleSubmit();
              }}
            >
              Try again
            </button>
          </div>
        ) : null}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.submit_button}
            disabled={submitDisabled}
            onClick={() => {
              void handleSubmit();
            }}
          >
            {submitState.kind === 'submitting'
              ? 'Sending to your case team…'
              : 'Submit for review →'}
          </button>
        </div>

        <p className={styles.legal_note} role="note">
          Greenbroad helps you prepare USCIS forms. You are the
          applicant and the filer — you sign the packet by hand and
          mail it yourself. We are not a law firm and do not provide
          legal advice. If your case has complications, talk to a
          licensed immigration attorney before filing.
        </p>
      </div>
    </section>
  );
}
